import type { KeyboardEvent, ReactNode } from 'react'

import { haptic } from '../telegram'

/** Стрелка «ведёт дальше» в углу подписи: без неё карточка не читается как проход. */
function Chevron() {
  return (
    <svg className="feed-chevron" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M5 3.5 8.5 7 5 10.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/**
 * Карточка ленты «Сегодня». Нажимается целиком, а не за стрелку: на телефоне в
 * стрелку не попасть.
 *
 * Это `div[role=button]`, а не `<button>`: внутри карточки живут абзацы и блок
 * оснований, а содержимое кнопки по стандарту — только строчное. Роль, tabIndex
 * и обработка Enter/Space возвращают то, что даёт настоящая кнопка, а haptic —
 * отклик Telegram. Без `onClick` карточка рисуется обычным блоком (журнал: у
 * него свои кнопки внутри, и вкладывать их в кликабельную область нельзя).
 */
export function FeedCard({
  label,
  labelColor,
  className,
  ariaLabel,
  onClick,
  children,
}: {
  label?: string
  labelColor?: string
  className?: string
  ariaLabel?: string
  onClick?: () => void
  children: ReactNode
}) {
  const classes = ['card', 'feed-card', className].filter(Boolean).join(' ')
  const head =
    label || onClick ? (
      <div className="feed-head">
        {label && (
          <span className="lbl" style={labelColor ? { color: labelColor } : undefined}>
            {label}
          </span>
        )}
        {onClick && <Chevron />}
      </div>
    ) : null

  if (!onClick) {
    return (
      <div className={classes}>
        {head}
        {children}
      </div>
    )
  }

  const activate = () => {
    haptic('light')
    onClick()
  }

  return (
    <div
      className={`${classes} feed-card-tap`}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={activate}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        activate()
      }}
    >
      {head}
      {children}
    </div>
  )
}
